/**
 * @fileoverview Firm setting: the minutes automatic drafting assumes for one
 * email, one file note and one document edit when nothing measured the
 * work (MOR-2334 AC-3). Every estimate is labelled and editable on review;
 * this only sets the starting figure and the cap.
 *
 * Dependencies:
 * - Convex: timesheets.settings.{getForOrg, setDraftEstimatePolicy}
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { extractErrorMessage } from "@/utils/errors";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { GROUP_CARD_CLASS } from "./settingsStyles";

const FIELDS = [
  ["emailMinutes", "Per email sent", "email-minutes"],
  ["noteMinutes", "Per file note or task", "note-minutes"],
  ["documentMinutes", "Per document edited", "document-minutes"],
] as const;

export function DraftEstimatePolicySection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  const setPolicy = useMutation(api.timesheets.settings.setDraftEstimatePolicy);
  const [drafts, setDrafts] = useState<
    Partial<Record<(typeof FIELDS)[number][0], string>>
  >({});

  const commit = async (key: (typeof FIELDS)[number][0]) => {
    const raw = drafts[key];
    if (raw === undefined || !settings) return;
    const minutes = Number(raw);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 240) {
      setDrafts((d) => ({ ...d, [key]: undefined }));
      return;
    }
    try {
      await setPolicy({
        orgId,
        ...settings.draftEstimatePolicy,
        [key]: minutes,
      });
      toast.success("Drafting estimate saved.");
      setDrafts((d) => ({ ...d, [key]: undefined }));
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b border-border/50 p-6">
        <h2 className="text-base font-semibold text-primary">
          Automatic drafting estimates
        </h2>
        <p className="mt-1 text-sm text-primary/60">
          When Morgan drafts time from activity that was not clocked, these are
          the minutes it assumes per item. Drafts are labelled as estimates,
          capped at twice this allowance, and always editable before they are
          accepted.
        </p>
      </div>
      {FIELDS.map(([key, label, id]) => (
        <div
          key={key}
          className="flex items-start justify-between gap-6 border-t border-border/50 p-6 first:border-t-0"
        >
          <Label htmlFor={id}>{label}</Label>
          <div className="flex items-center gap-2">
            <Input
              id={id}
              type="number"
              min={1}
              max={240}
              step={1}
              className="w-24 tabular-nums"
              value={
                drafts[key] ??
                (settings ? String(settings.draftEstimatePolicy[key]) : "")
              }
              onChange={(e) =>
                setDrafts((d) => ({ ...d, [key]: e.target.value }))
              }
              onBlur={() => void commit(key)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commit(key);
              }}
              disabled={!canEdit || settings === undefined}
            />
            <span className="text-sm text-muted-foreground">min</span>
          </div>
        </div>
      ))}
    </div>
  );
}
